import React, { useState } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as BatchIcon,
} from '@mui/icons-material';
import { ItemBatch } from '../../settings/items/types/item-batch.types';
import { itemBatchApi } from '../../settings/items/api/itemBatchApi';
import { useSnackbar } from 'notistack';

interface BatchSelectorProps {
  itemId: string;
  availableBatches: ItemBatch[];
  selectedBatchNumber: string;
  onBatchChange: (batchNumber: string, expiryDate?: string) => void;
  onBatchesUpdated: (batches: ItemBatch[]) => void;
  disabled?: boolean;
  transactionType?: string;
}

const BatchSelector: React.FC<BatchSelectorProps> = ({
  itemId,
  availableBatches,
  selectedBatchNumber,
  onBatchChange,
  onBatchesUpdated,
  disabled = false,
  transactionType
}) => {
  // Debug logging
  console.log('🔍 BatchSelector - itemId:', itemId);
  console.log('🔍 BatchSelector - availableBatches:', availableBatches);
  console.log('🔍 BatchSelector - selectedBatchNumber:', selectedBatchNumber);
  console.log('🔍 BatchSelector - transactionType:', transactionType);
  console.log('🔍 BatchSelector - disabled:', disabled);
  const { enqueueSnackbar } = useSnackbar();
  const [showAddBatchDialog, setShowAddBatchDialog] = useState(false);
  const [newBatch, setNewBatch] = useState({
    batchNumber: '',
    expiryDate: '',
    status: 'ACTIVE' as const
  });
  const [saving, setSaving] = useState(false);

  // Handle batch selection
  const handleBatchSelection = (value: string | ItemBatch | null) => {
    console.log('🔍 BatchSelector - handleBatchSelection value:', value);
    
    if (typeof value === 'string') {
      // User typed a new batch number
      if (value.trim()) {
        console.log('🔍 BatchSelector - User entered new batch number:', value);
        onBatchChange(value);
      } else {
        // Empty string, clear selection
        onBatchChange('');
      }
    } else if (value) {
      // User selected an existing batch
      console.log('🔍 BatchSelector - User selected existing batch:', value);
      onBatchChange(value.batchNumber, value.expiryDate || undefined);
    } else {
      // Value is null/undefined
      console.log('🔍 BatchSelector - Selection cleared');
      onBatchChange('');
    }
  };

  // Handle add new batch
  const handleAddBatch = async () => {
    if (!newBatch.batchNumber.trim()) {
      enqueueSnackbar('Please enter a batch number', { variant: 'error' });
      return;
    }

    try {
      setSaving(true);
      const batchData = {
        itemId,
        batchNumber: newBatch.batchNumber,
        expiryDate: newBatch.expiryDate || undefined,
        status: newBatch.status,
        isActive: true
      };

      const createdBatch = await itemBatchApi.createItemBatch(batchData);
      
      // Update the available batches
      const updatedBatches = [...availableBatches, createdBatch];
      onBatchesUpdated(updatedBatches);
      
      // Select the newly created batch
      onBatchChange(createdBatch.batchNumber, createdBatch.expiryDate || undefined);
      
      // Reset form and close dialog
      setNewBatch({ batchNumber: '', expiryDate: '', status: 'ACTIVE' });
      setShowAddBatchDialog(false);
      
      enqueueSnackbar('Batch created successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Error creating batch:', error);
      enqueueSnackbar(error.message || 'Failed to create batch', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Autocomplete
        options={availableBatches}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.batchNumber}
        value={availableBatches.find(b => b.batchNumber === selectedBatchNumber) || null}
        onChange={(_, value) => handleBatchSelection(value)}
        freeSolo
        disabled={disabled || !itemId} // Don't disable even if no batches, to allow manual entry
        renderInput={(params) => {
          // Check if this is for an incoming transaction
          const isIncoming = [
            'RECEIVED', 
            'RETURNED', 
            'ADJUSTMENT_IN',
            'TRANSFER_IN'
          ].includes(transactionType || '');
          
          return (
            <TextField 
              {...params} 
              size="small" 
              required 
              placeholder={
                !itemId ? "Select item first" : 
                availableBatches.length === 0 ? 
                  (isIncoming ? "Type new batch or click + to add" : "Type batch number or click + to add") : 
                  "Select or type batch number"
              }
              helperText={
                itemId && availableBatches.length === 0 ? 
                  (isIncoming ? 
                    "No existing batches found for this item" : 
                    "No batches with stock found, but you can still enter a batch number") : 
                  ""
              }
            />
          );
        }}
        sx={{ minWidth: 150, flex: 1 }}
      />
      
      {itemId && (
        <Tooltip title={
          availableBatches.length === 0 ? 
            (transactionType && ['DISPOSED', 'DISPENSED_TO_PATIENT', 'TRANSFER'].includes(transactionType) ? 
              "Click to add a new batch" : 
              "Click to add a new batch") : 
            "Add new batch"
        }>
          <IconButton
            size="small"
            onClick={() => setShowAddBatchDialog(true)}
            color={availableBatches.length === 0 ? "error" : "primary"}
            disabled={disabled}
          >
            {availableBatches.length === 0 ? <BatchIcon /> : <AddIcon />}
          </IconButton>
        </Tooltip>
      )}

      {/* Add Batch Dialog */}
      <Dialog open={showAddBatchDialog} onClose={() => setShowAddBatchDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Batch</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Batch Number"
                value={newBatch.batchNumber}
                onChange={(e) => setNewBatch(prev => ({ ...prev, batchNumber: e.target.value }))}
                required
                placeholder="Enter batch number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                value={newBatch.expiryDate}
                onChange={(e) => setNewBatch(prev => ({ ...prev, expiryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={newBatch.status}
                onChange={(e) => setNewBatch(prev => ({ ...prev, status: e.target.value as any }))}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="EXPIRED">Expired</MenuItem>
                <MenuItem value="RECALLED">Recalled</MenuItem>
                <MenuItem value="DISPOSED">Disposed</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddBatchDialog(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleAddBatch} variant="contained" disabled={saving}>
            {saving ? 'Creating...' : 'Create Batch'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchSelector;
